import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { 
  TextField, 
  Button, 
  CircularProgress, 
  FormControl, 
  InputLabel, 
  Select, 
  MenuItem,
  Typography,
  Alert,
  Box,
  Paper,
  Grid,
  Chip,
  LinearProgress
} from '@mui/material';
// Using standard inputs for date and time instead of MUI pickers
import { doc, getDoc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { ref, uploadBytesResumable, getDownloadURL } from 'firebase/storage';
import { db, storage } from '../../firebase';
import { useAuth } from '../../contexts/AuthContext';
import { FaCalendarAlt, FaImage, FaMoneyBillWave, FaInfoCircle, FaSave, FaLink } from 'react-icons/fa';

const Container = styled.div`
  padding: 2rem;
  background: rgba(255, 255, 255, 0.05);
  border-radius: 15px;
  margin-bottom: 2rem;
`;

const Title = styled.h1`
  color: white;
  font-size: 2rem;
  margin-bottom: 2rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  
  svg {
    color: #4b7bec;
  }
`;

const FormSection = styled(Paper)`
  padding: 2rem;
  margin-bottom: 2rem;
  background-color: rgba(255, 255, 255, 0.05) !important;
  border-radius: 12px !important;
`;

const SectionTitle = styled(Typography)`
  color: white !important;
  font-size: 1.2rem !important;
  margin-bottom: 1.5rem !important;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  
  svg {
    color: #4b7bec;
  }
`;

const StyledTextField = styled(TextField)`
  margin-bottom: 1.5rem !important;
  
  .MuiInputBase-root {
    color: white !important;
  }
  
  .MuiInputLabel-root {
    color: rgba(255, 255, 255, 0.7) !important;
  }
  
  .MuiOutlinedInput-notchedOutline {
    border-color: rgba(255, 255, 255, 0.2) !important;
  }
  
  &:hover .MuiOutlinedInput-notchedOutline {
    border-color: rgba(255, 255, 255, 0.5) !important;
  }
  
  &.Mui-focused .MuiOutlinedInput-notchedOutline {
    border-color: #4b7bec !important;
  }
`;

const StyledFormControl = styled(FormControl)`
  margin-bottom: 1.5rem !important;
  width: 100% !important;
  
  .MuiInputBase-root {
    color: white !important;
  }
  
  .MuiInputLabel-root {
    color: rgba(255, 255, 255, 0.7) !important;
  }
  
  .MuiOutlinedInput-notchedOutline {
    border-color: rgba(255, 255, 255, 0.2) !important;
  }
  
  &:hover .MuiOutlinedInput-notchedOutline {
    border-color: rgba(255, 255, 255, 0.5) !important;
  }
  
  &.Mui-focused .MuiOutlinedInput-notchedOutline {
    border-color: #4b7bec !important;
  }
`;

const ImagePreview = styled.div`
  margin-bottom: 1.5rem;
  border-radius: 8px;
  overflow: hidden;
  position: relative;
  width: 100%;
  max-width: 400px;
  
  img {
    width: 100%;
    height: auto;
    display: block;
  }
  
  .overlay {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.5);
    display: flex;
    justify-content: center;
    align-items: center;
    opacity: 0;
    transition: opacity 0.3s;
  }
  
  &:hover .overlay {
    opacity: 1;
  }
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 3rem;
`;

const EditEventForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  // Form state
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [date, setDate] = useState(null);
  const [time, setTime] = useState(null);
  const [duration, setDuration] = useState('');
  const [price, setPrice] = useState('');
  const [premiumPrice, setPremiumPrice] = useState('');
  const [category, setCategory] = useState('');
  const [image, setImage] = useState(null);
  const [imageUrl, setImageUrl] = useState('');
  const [imageUploadProgress, setImageUploadProgress] = useState(0);
  const [streamUrl, setStreamUrl] = useState('');
  const [capacity, setCapacity] = useState('');
  const [originalEvent, setOriginalEvent] = useState(null);
  
  // Fetch event data
  useEffect(() => {
    const fetchEvent = async () => {
      try {
        setLoading(true);
        
        const eventDoc = await getDoc(doc(db, 'streamEvents', id));
        
        if (!eventDoc.exists()) {
          setError('Event not found');
          setLoading(false);
          return;
        }
        
        const eventData = eventDoc.data();
        
        // Check if the current user is the creator
        if (eventData.creatorId !== currentUser.uid) {
          setError('You do not have permission to edit this event');
          setLoading(false);
          return;
        }
        
        setOriginalEvent(eventData);
        
        // Set form values
        setTitle(eventData.title || '');
        setDescription(eventData.description || '');
        setDate(eventData.date || '');
        setTime(eventData.time || '');
        setDuration(eventData.duration || '');
        setPrice(eventData.price || '');
        setPremiumPrice(eventData.premiumPrice || '');
        setCategory(eventData.category || '');
        setImageUrl(eventData.image || '');
        setStreamUrl(eventData.streamUrl || '');
        setCapacity(eventData.capacity || '');
        
      } catch (error) {
        console.error('Error fetching event:', error);
        setError('Failed to load event data');
      } finally {
        setLoading(false);
      }
    };
    
    if (id && currentUser) {
      fetchEvent();
    }
  }, [id, currentUser]);
  
  const handleImageChange = (e) => {
    if (e.target.files[0]) {
      setImage(e.target.files[0]);
      
      // Create a preview URL
      const reader = new FileReader();
      reader.onload = () => {
        setImageUrl(reader.result);
      };
      reader.readAsDataURL(e.target.files[0]);
    }
  };
  
  const uploadImage = async () => {
    return new Promise((resolve, reject) => {
      if (!image) {
        // If no new image was selected, return the existing URL
        resolve(imageUrl);
        return;
      }
      
      const storageRef = ref(storage, `streamEvents/${id}_${image.name}`);
      const uploadTask = uploadBytesResumable(storageRef, image);
      
      uploadTask.on(
        'state_changed',
        (snapshot) => {
          const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
          setImageUploadProgress(progress);
        },
        (error) => {
          console.error('Error uploading image:', error);
          reject(error);
        },
        async () => {
          const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
          resolve(downloadURL);
        }
      );
    });
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      setSaving(true);
      setError('');
      setSuccess('');
      
      // Validate form
      if (!title || !description || !date || !time || !price || !category) {
        setError('Please fill in all required fields');
        setSaving(false);
        return;
      }
      
      // Date and time are already in the correct format from the input fields
      
      // Upload image if changed
      const imageURL = await uploadImage();
      
      // Update event in Firestore
      const eventRef = doc(db, 'streamEvents', id);
      
      await updateDoc(eventRef, {
        title,
        description,
        date,
        time,
        duration,
        price: Number(price),
        premiumPrice: premiumPrice ? Number(premiumPrice) : null,
        category,
        image: imageURL,
        streamUrl,
        capacity: capacity ? Number(capacity) : null,
        updatedAt: serverTimestamp()
      });
      
      setSuccess('Event updated successfully');
      
      // Navigate back to event management after a short delay
      setTimeout(() => {
        navigate('/streaming-events/manage-events');
      }, 2000);
      
    } catch (error) {
      console.error('Error updating event:', error);
      setError('Failed to update event');
    } finally {
      setSaving(false);
    }
  };
  
  if (loading) {
    return (
      <LoadingContainer>
        <CircularProgress style={{ color: '#4b7bec' }} />
      </LoadingContainer>
    );
  }
  
  return (
    <Container>
      <Title>
        <FaCalendarAlt /> Edit Event
      </Title>
      
      {error && (
        <Alert severity="error" sx={{ mb: 3, backgroundColor: 'rgba(211, 47, 47, 0.1)' }}>
          {error}
        </Alert>
      )}
      
      {success && (
        <Alert severity="success" sx={{ mb: 3, backgroundColor: 'rgba(46, 125, 50, 0.1)' }}>
          {success}
        </Alert>
      )}
      
      <form onSubmit={handleSubmit}>
        <FormSection>
          <SectionTitle>
            <FaInfoCircle /> Basic Information
          </SectionTitle>
          
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <StyledTextField
                label="Event Title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                fullWidth
                required
              />
            </Grid>
            
            <Grid item xs={12}>
              <StyledTextField
                label="Description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                fullWidth
                multiline
                rows={4}
                required
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <StyledFormControl>
                <InputLabel>Category</InputLabel>
                <Select
                  value={category}
                  onChange={(e) => setCategory(e.target.value)}
                  label="Category"
                  required
                >
                  <MenuItem value="music">Music</MenuItem>
                  <MenuItem value="comedy">Comedy</MenuItem>
                  <MenuItem value="education">Education</MenuItem>
                  <MenuItem value="business">Business</MenuItem>
                  <MenuItem value="lifestyle">Lifestyle</MenuItem>
                  <MenuItem value="other">Other</MenuItem>
                </Select>
              </StyledFormControl>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <StyledTextField
                label="Capacity (optional)"
                type="number"
                value={capacity}
                onChange={(e) => setCapacity(e.target.value)}
                fullWidth
                InputProps={{ inputProps: { min: 0 } }}
              />
            </Grid>
          </Grid>
        </FormSection>
        
        <FormSection>
          <SectionTitle>
            <FaCalendarAlt /> Date & Time
          </SectionTitle>
          
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <StyledTextField
                label="Event Date"
                type="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                fullWidth
                required
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            
            <Grid item xs={12} md={4}>
              <StyledTextField
                label="Start Time"
                type="time"
                value={time}
                onChange={(e) => setTime(e.target.value)}
                fullWidth
                required
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            
            <Grid item xs={12} md={4}>
              <StyledTextField
                label="Duration (e.g., 2 hours)"
                value={duration}
                onChange={(e) => setDuration(e.target.value)}
                fullWidth
              />
            </Grid>
          </Grid>
        </FormSection>
        
        <FormSection>
          <SectionTitle>
            <FaMoneyBillWave /> Pricing
          </SectionTitle>
          
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <StyledTextField
                label="Standard Ticket Price ($)"
                type="number"
                value={price}
                onChange={(e) => setPrice(e.target.value)}
                fullWidth
                required
                InputProps={{ inputProps: { min: 0, step: 0.01 } }}
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <StyledTextField
                label="Premium Ticket Price ($) (optional)"
                type="number"
                value={premiumPrice}
                onChange={(e) => setPremiumPrice(e.target.value)}
                fullWidth
                InputProps={{ inputProps: { min: 0, step: 0.01 } }}
              />
            </Grid>
          </Grid>
        </FormSection>
        
        <FormSection>
          <SectionTitle>
            <FaImage /> Event Image
          </SectionTitle>
          
          {imageUrl && (
            <ImagePreview>
              <img src={imageUrl} alt="Event preview" />
              <div className="overlay">
                <Button
                  variant="contained"
                  component="label"
                  sx={{
                    backgroundColor: 'rgba(75, 123, 236, 0.8)',
                    '&:hover': { backgroundColor: 'rgba(75, 123, 236, 1)' }
                  }}
                >
                  Change Image
                  <input
                    type="file"
                    hidden
                    accept="image/*"
                    onChange={handleImageChange}
                  />
                </Button>
              </div>
            </ImagePreview>
          )}
          
          {!imageUrl && (
            <Button
              variant="contained"
              component="label"
              startIcon={<FaImage />}
              sx={{
                mb: 2,
                backgroundColor: '#4b7bec',
                '&:hover': { backgroundColor: '#3867d6' }
              }}
            >
              Upload Image
              <input
                type="file"
                hidden
                accept="image/*"
                onChange={handleImageChange}
              />
            </Button>
          )}
          
          {imageUploadProgress > 0 && imageUploadProgress < 100 && (
            <Box sx={{ width: '100%', mb: 2 }}>
              <Typography variant="body2" color="white">
                Uploading: {Math.round(imageUploadProgress)}%
              </Typography>
              <LinearProgress
                variant="determinate"
                value={imageUploadProgress}
                sx={{
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                  '& .MuiLinearProgress-bar': {
                    backgroundColor: '#4b7bec'
                  }
                }}
              />
            </Box>
          )}
        </FormSection>
        
        <FormSection>
          <SectionTitle>
            <FaLink /> Streaming Details
          </SectionTitle>
          
          <StyledTextField
            label="Stream URL (will be shown to ticket holders)"
            value={streamUrl}
            onChange={(e) => setStreamUrl(e.target.value)}
            fullWidth
            placeholder="https://yourstreamingplatform.com/your-event"
          />
          
          <Typography variant="body2" color="rgba(255, 255, 255, 0.7)" sx={{ mt: 1 }}>
            Note: This URL will only be visible to users who have purchased tickets.
          </Typography>
        </FormSection>
        
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
          <Button
            variant="outlined"
            onClick={() => navigate('/streaming-events/manage-events')}
            sx={{
              color: 'white',
              borderColor: 'rgba(255, 255, 255, 0.3)',
              '&:hover': { borderColor: 'white' }
            }}
          >
            Cancel
          </Button>
          
          <Button
            type="submit"
            variant="contained"
            disabled={saving}
            startIcon={saving ? <CircularProgress size={20} /> : <FaSave />}
            sx={{
              backgroundColor: '#4b7bec',
              '&:hover': { backgroundColor: '#3867d6' }
            }}
          >
            {saving ? 'Saving...' : 'Save Changes'}
          </Button>
        </Box>
      </form>
    </Container>
  );
};

export default EditEventForm;
